import type { ContributionDetail } from '../services/api';
import type { Coordinates, GeoJSONGeometry, Place } from '../types';
import { composeAddressLine,
  composeBuildingAddressLine,
  composePlaceContextLine,
} from './contributionAddress';
import { contributionFeatureShowsBusinessDetails } from './contributionPlaceAdmin';
import { normalizeBuildingUnits } from './buildingUnitsReview';
import { TRANSIT_STOP_PLACE_TYPE, transitStopFieldsFromExtras } from './transitStop';

export const CONTRIBUTION_PREVIEW_PLACE_PREFIX = 'contrib-preview:';

export function isContributionPreviewPlaceId(id: string | undefined | null): boolean {
  return Boolean(id?.startsWith(CONTRIBUTION_PREVIEW_PLACE_PREFIX));
}

export function contributionGeometryCenter(
  geometry: GeoJSONGeometry | undefined | null,
): Coordinates | null {
  if (!geometry?.coordinates) return null;

  if (geometry.type === 'Point') {
    const coords = geometry.coordinates as number[];
    if (coords.length < 2) return null;
    return { lng: coords[0], lat: coords[1] };
  }

  if (geometry.type === 'LineString') {
    const coords = geometry.coordinates as number[][];
    if (!coords.length) return null;
    const mid = coords[Math.floor(coords.length / 2)];
    return { lng: mid[0], lat: mid[1] };
  }

  if (geometry.type === 'Polygon') {
    const ring = geometry.coordinates[0] as number[][] | undefined;
    if (!ring?.length) return null;
    let sumLat = 0;
    let sumLng = 0;
    for (const point of ring) {
      sumLng += point[0];
      sumLat += point[1];
    }
    const count = ring.length;
    return { lng: sumLng / count, lat: sumLat / count };
  }

  return null;
}

function buildContributionPreviewAddress(detail: ContributionDetail): string {
  const featureType = detail.feature_type || '';
  const extra = detail.extra_fields ?? {};
  const ghanaPostGps = typeof extra.ghana_post_gps === 'string' ? extra.ghana_post_gps : '';
  const houseNumber =
    typeof extra.house_number === 'string'
      ? extra.house_number
      : typeof extra.building_house_number === 'string'
        ? extra.building_house_number
        : undefined;
  const frontageStreetAddress =
    typeof extra.frontage_street_address === 'string'
      ? extra.frontage_street_address
      : typeof extra.building_frontage_street_address === 'string'
        ? extra.building_frontage_street_address
        : undefined;

  if (featureType === 'place') {
    return composePlaceContextLine({
      placeName: detail.name,
      suburb: detail.addr_suburb ?? '',
      city: detail.addr_city ?? '',
      district: detail.addr_district ?? '',
      region: detail.addr_region ?? '',
    });
  }

  if (featureType === 'building' || featureType === 'poi' || featureType === 'landmark') {
    return composeBuildingAddressLine({
      name: detail.name,
      street: detail.addr_street ?? '',
      suburb: detail.addr_suburb ?? '',
      city: detail.addr_city ?? '',
      district: detail.addr_district ?? '',
      ghanaPostGps,
      houseNumber,
      frontageStreetAddress,
    });
  }

  if (detail.address?.trim()) return detail.address.trim();

  return composeAddressLine({
    name: detail.name,
    street: detail.addr_street ?? '',
    suburb: detail.addr_suburb ?? '',
    city: detail.addr_city ?? '',
    district: detail.addr_district ?? '',
    region: detail.addr_region ?? '',
    ghanaPostGps,
  });
}

export function contributionDetailToPreviewPlace(detail: ContributionDetail): Place {
  const coordinates = contributionGeometryCenter(detail.geometry as GeoJSONGeometry);
  if (!coordinates) {
    throw new Error('Contribution has no mappable geometry');
  }

  const extra = detail.extra_fields ?? {};
  const featureType = detail.feature_type || 'poi';
  const landmarkSourceGroup =
    typeof extra.landmark_source_group === 'string' ? extra.landmark_source_group : null;
  const showBusinessDetails = contributionFeatureShowsBusinessDetails(featureType, landmarkSourceGroup);
  const placeType = featureType === 'landmark'
    ? 'poi'
    : featureType === 'transport_stop'
      ? TRANSIT_STOP_PLACE_TYPE
      : featureType;
  const address = buildContributionPreviewAddress(detail);
  const transitFields = featureType === 'transport_stop'
    ? transitStopFieldsFromExtras(extra)
    : {};

  return {
    id: `${CONTRIBUTION_PREVIEW_PLACE_PREFIX}${detail.id}`,
    name: detail.name,
    category: detail.category || 'other',
    placeType,
    address,
    addrStreet: detail.addr_street,
    addrSuburb: detail.addr_suburb,
    addrCity: detail.addr_city,
    district: detail.addr_district,
    region: detail.addr_region,
    coordinates,
    geometry: detail.geometry as GeoJSONGeometry,
    phone: showBusinessDetails ? detail.phone : undefined,
    whatsapp: showBusinessDetails && typeof extra.whatsapp === 'string' ? extra.whatsapp : undefined,
    email: showBusinessDetails && typeof extra.email === 'string' ? extra.email : undefined,
    website: showBusinessDetails ? detail.website : undefined,
    facebook: showBusinessDetails && typeof extra.facebook === 'string' ? extra.facebook : undefined,
    instagram: showBusinessDetails && typeof extra.instagram === 'string' ? extra.instagram : undefined,
    openingHours: showBusinessDetails && typeof extra.opening_hours === 'string' ? extra.opening_hours : undefined,
    description: detail.description,
    descriptionSourceUrl:
      typeof extra.description_source_url === 'string' ? extra.description_source_url : undefined,
    ghanaPostGPS: typeof extra.ghana_post_gps === 'string' ? extra.ghana_post_gps : undefined,
    photos: detail.photos,
    buildingUnits: normalizeBuildingUnits(detail.building_units),
    ...transitFields,
  };
}
